export const INDEXES_TABLE_PAGE_SIZE = 5;
export const INDEX_PIPELINES_TABLE_PAGE_SIZE = 3;
export const INDEX_CONNECTED_PIPELINES_LIMIT = 100;
export const LIMIT_DEPLOYED_PIPELINES_CONNECTED = 1;

export const EMPTY_INDEXES_LIST_SECTION = {
  TITLE: 'Indexes',
  DESCRIPTION: [
    {
      paragraph:
        'An Index prepares files for search by converting them into smaller text passages (Documents) and storing them in a DocumentStore. Files are indexed once during deployment, with new files automatically indexed as they are uploaded to your workspace. After creating Indexes, you can link them to the desired pipeline(s). A single Index can be connected to one or multiple pipelines, and multiple Indexes can be used within a single pipeline.',
    },
  ],
};

export const INDEXES_TABLE_COLUMNS_TITLE = {
  NAME: 'Name',
  CREATED_AT: 'Created at',
  CREATED_BY: 'Created by',
  STATUS: 'Status',
};

export const INDEX_STATUS_MESSAGES = {
  INDEXED: 'Indexed',
  NOT_INDEXED: 'Not Indexed',
  IN_PROGRESS: 'Indexing',
  PARTIALLY_INDEXED: 'Partially Indexed',
  FAILED_INDEXING: 'Failed to index',
};
export const INDEXING_STATUS_POPOVER_HEADER = 'File Indexing Status';
export const INDEX_STATUS_POP_CONTENT = {
  PENDING: 'Pending Tasks',
  INDEXED: 'Indexed',
  SKIPPED: 'Skipped',
  FAILED: 'Failed',
  TOTAL: 'Total',
};

export enum IndexesListTab {
  ALL = 'ALL',
  ACTIVE = 'ACTIVE',
  DRAFTS = 'DRAFTS',
}

export const INDEXES_LIST_TABS_OPTIONS = [
  { key: IndexesListTab.ALL, label: 'All' },
  { key: IndexesListTab.ACTIVE, label: 'Active' },
  { key: IndexesListTab.DRAFTS, label: 'Drafts' },
];

export enum IndexMenuAction {
  Edit = 'EDIT',
  Enable = 'ENABLE',
  Disable = 'DISABLE',
  Duplicate = 'DUPLICATE',
  Rename = 'RENAME',
  Delete = 'DELETE',
  Download = 'DOWNLOAD',
}

export const ENABLE_INDEX_BUTTON_LABEL = 'Enable';
export const DISABLE_INDEX_BUTTON_LABEL = 'Disable';
export const RENAME_INDEX_BUTTON_LABEL = 'Rename';
export const DELETE_INDEX_BUTTON_LABEL = 'Delete';
export const DUPLICATE_INDEX_BUTTON_LABEL = 'Duplicate';
export const DOWNLOAD_INDEX_DOCUMENTS_BUTTON_LABEL = 'Export documents as CSV';
export const RENAME_INDEX_BUTTON_ALERT_TOOLTIP = 'Cannot rename an enabled index';

export const INDEX_STATS_KEYS = {
  PENDING_FILES: 'pendingFiles',
  FAILED_FILES: 'failedFiles',
  SKIPPED_FILES: 'skippedFiles',
  INDEXED_FILES: 'indexedFiles',
  TOTAL_DOCUMENTS: 'totalDocuments',
  TOTAL_FILES: 'totalFiles',
};

export const INDEX_STATS_LABELS = [
  {
    key: INDEX_STATS_KEYS.TOTAL_FILES,
    label: 'Total files',
    description: `The number of uploaded files to be indexed.`,
  },
  {
    key: INDEX_STATS_KEYS.INDEXED_FILES,
    label: 'Indexed files',
    description: `The number of files successfully indexed into the document store.`,
  },
  {
    key: INDEX_STATS_KEYS.TOTAL_DOCUMENTS,
    label: 'Documents created',
    description: `The count of documents created from the files and indexed into the document store.`,
  },
  {
    key: INDEX_STATS_KEYS.PENDING_FILES,
    label: 'Pending tasks',
    description: `Shows the count of ongoing tasks, like adding files to the document store or updating metadata.`,
  },
  {
    key: INDEX_STATS_KEYS.SKIPPED_FILES,
    label: 'Skipped files',
    description: `The number of files that were ignored during indexing. For details, see %logs%.`,
  },
  {
    key: INDEX_STATS_KEYS.FAILED_FILES,
    label: 'Failed files',
    description: `The count of files that encountered errors during indexing. For details, see %logs%.`,
  },
];

export const INDEX_STATS_RETRY_BUTTON_LABEL = 'Retry';
export const INDEX_STATS_SKIPPED_FILES_LABEL = '%value% skipped';
export const INDEX_STATS_FAILED_FILES_LABEL = '%value% failed';

export enum IndexDetailsTab {
  FILES = 'FILES',
  LOGS = 'LOGS',
}

export const INDEX_DETAILS_TAB_LABELS = {
  [IndexDetailsTab.FILES]: 'Files',
  [IndexDetailsTab.LOGS]: 'Logs',
};

export const INDEX_DETAILS_CONNECTED_PIPELINES_TITLE = 'Pipelines using this index';
export const INDEX_DETAILS_CONNECTED_PIPELINES_EMPTY_TEXT = 'No pipelines are using this index';
export const INDEX_DETAILS_CONNECTED_PIPELINES_VIEW_BUTTON_TOOLTIP = 'View pipeline details';

export enum IndexDetailsConnectedPipelinesSource {
  CONNECTED_PIPELINES_CARD = 'card',
  CONNECTED_PIPELINES_MODAL = 'modal',
  CHECK_DEPLOYED_PIPELINES = 'check',
}

// Index Files table

export const INDEX_FILES_TABLE_PAGE_SIZE = 10;
export const INDEX_FILES_EMPTY_TABLE_MESSAGE = 'There are no files processed by this index.';
export const VIEW_FILE_BUTTON_LABEL = 'View';

export const INDEX_FILES_TABLE_COLUMNS_TITLE = {
  NAME: 'Name',
  SIZE: 'Size',
  STATUS: 'Status',
  UPLOADED: 'Uploaded',
};

export enum IndexFileStatus {
  INDEXED = 'INDEXED',
  FAILED = 'FAILED',
  NO_DOCUMENTS = 'NO_DOCUMENTS',
}

export const INDEX_FILE_STATUS_TAG_LABELS = {
  [IndexFileStatus.INDEXED]: 'Indexed',
  [IndexFileStatus.FAILED]: 'Failed to index',
  [IndexFileStatus.NO_DOCUMENTS]: 'Skipped',
};

export enum IndexFilesFilters {
  STATUS = 'meta._file_transparency_status',
}

export const INDEX_FILES_FILTERS_OPTIONS = [{ key: IndexFilesFilters.STATUS, label: 'Status' }];

export enum IndexFilesTableMoreActionsMenuItems {
  RetryIndexing = 'RETRY_INDEXING',
}

export const INDEX_FILES_TABLE_MORE_ACTIONS_MENU_ITEMS_LABELS = {
  [IndexFilesTableMoreActionsMenuItems.RetryIndexing]: 'Retry indexing',
};

// Document preview

export const DOCUMENT_PREVIEW_PREV_FILE_BUTTON_LABEL = 'Prev. file';
export const DOCUMENT_PREVIEW_NEXT_FILE_BUTTON_LABEL = 'Next file';
export const DOCUMENT_PREVIEW_RETRIEVAL_CHARACTERS_LABEL = 'Characters';
export const DOCUMENT_PREVIEW_SHOW_LESS_BUTTON_LABEL = 'Show less';
export const DOCUMENT_PREVIEW_SHOW_MORE_BUTTON_LABEL = 'Show more';
export const DOCUMENT_PREVIEW_DISABLED_DOCUMENTS_TAB_TOOLTIP = `This file wasn't indexed so there aren't any documents created from it.`;
export const DOCUMENT_PREVIEW_TOTAL_DOCUMENTS_LABEL_PLURAL = 'documents created';
export const DOCUMENT_PREVIEW_TOTAL_DOCUMENTS_LABEL_SINGULAR = 'document created';
export const MORE_THAN_1000_DOCUMENTS_LABEL = '1000+';
export const DOCUMENT_PREVIEW_COPY_ERROR_BUTTON_LABEL = 'Copy to clipboard';

export const DOCUMENTS_PREVIEW_TABS_KEYS = {
  FILE: 'file',
  DOCUMENTS: 'documents',
  ERRORS: 'errors',
};

export const DOCUMENTS_PREVIEW_TABS = [
  { key: DOCUMENTS_PREVIEW_TABS_KEYS.FILE, label: 'File' },
  { key: DOCUMENTS_PREVIEW_TABS_KEYS.DOCUMENTS, label: 'Documents' },
  { key: DOCUMENTS_PREVIEW_TABS_KEYS.ERRORS, label: 'Errors' },
];

export const CONNECTED_PIPELINES_TITLE = 'Pipelines using this index';

export const INDEX_ENABLED_SUCCESS_MESSAGE = 'Index enabled successfully';
export const INDEX_DISABLED_SUCCESS_MESSAGE = 'Index disabled successfully';
export const INDEX_DELETED_SUCCESS_MESSAGE = 'Index deleted successfully';
export const INDEX_LOGS_DOWNLOAD_SUCCESS_MESSAGE =
  'Index logs successfully downloaded to %fileName%';
export const INDEX_SAVED_SUCCESS_MESSAGE = 'Index saved successfully';
export const INDEX_SAVED_AND_ENABLED_SUCCESS_MESSAGE = 'Index saved and enabled successfully';

// create index

export const CREATE_INDEX_BUTTON_LABEL = 'Create Index';
export const CREATE_INDEX_MODAL_HEADER = 'Create an Index';
export const CREATE_INDEX_MODAL_SELECT_TEMPLATE_HEADER_DESCRIPTION =
  'Choose a template that matches your file needs. Templates are curated and maintained bydeepset. An index preprocesses all files in your workspace, converts them into documents, and stores those documents in a document store.';
export const CREATE_INDEX_MODAL_BUILD_YOUR_OWN_BUTTON_LABEL = 'Build your own';

export const CREATE_INDEX_MODAL_BUILD_YOUR_OWN_INFO = {
  TITLE: 'Build your own',
  DESCRIPTION: 'Start with an empty canvas and choose the components for your index.',
};

export const CREATE_INDEX_MODAL_FORM = {
  NAME: {
    LABEL: 'Name',
    PLACEHOLDER: 'Enter index name',
  },
  DESCRIPTION: {
    LABEL: 'Description',
    PLACEHOLDER: 'Describe the purpose of this index',
  },
};

export enum CreateIndexModalStep {
  SELECT_TEMPLATE = 'selectTemplate',
  CREATE_INDEX = 'createIndex',
}

// Sorting constants

export const INDEXES_LIST_DEFAULT_SORTING_KEY_ALL_TAB = 'default';

export const INDEXES_LIST_SORTING_DATATABLE_OPTIONS = [
  { key: 'created_at_desc', label: 'Newest to oldest' },
  { key: 'created_at_asc', label: 'Oldest to newest' },
  { key: 'created_by_asc', label: 'Created by ascending' },
  { key: 'created_by_desc', label: 'Created by descending' },
];

export const INDEXES_LIST_SORTING_PARAMS_BY_KEY = {
  default: {
    field: '',
    order: '',
  },
  created_at_desc: {
    field: 'created_at',
    order: 'DESC',
  },
  created_at_asc: {
    field: 'created_at',
    order: 'ASC',
  },
  created_by_asc: {
    field: 'created_by/given_name',
    order: 'ASC',
  },
  created_by_desc: {
    field: 'created_by/given_name',
    order: 'DESC',
  },
};

// Index confirm action modal
export const CONFIRM_MODAL_LABELS = {
  [IndexMenuAction.Delete]: {
    MODAL_TITLE: 'Delete Index',
    MODAL_TITLE_ALTERNATE: 'Cannot delete index',
    PRIMARY_BUTTON_LABEL: 'Delete',
    PRIMARY_BUTTON_LABEL_ALTERNATE: 'Got it',
    DESCRIPTION:
      'Deleting the %indexName% is permanent. Pipelines using this index will lose access to its data.',
    DESCRIPTION_ALTERNATE:
      'Deleting this index could disrupt active pipelines that are using it. To delete, remove the index from those pipelines or undeploy them.',
  },
  [IndexMenuAction.Disable]: {
    MODAL_TITLE: 'Disable Index',
    MODAL_TITLE_ALTERNATE: 'Cannot disable index',
    PRIMARY_BUTTON_LABEL: 'Disable',
    PRIMARY_BUTTON_LABEL_ALTERNATE: 'Got it',
    DESCRIPTION:
      'Disabling the %indexName% removes all indexed data. If you re-enable it, indexing will start from the beginning. During processing, pipelines can only search files that have already been indexed.',
    DESCRIPTION_ALTERNATE:
      'Disabling this index could disrupt active pipelines that are using it. To disable, remove the index from those pipelines or undeploy them.',
  },
};

export const CONFIRM_MODAL_PIPELINES_EMPTY_LABEL = 'There are no pipelines using this index.';
export const CONFIRM_MODAL_INDEX_NAME_INPUT_LABEL = 'Type the index name to confirm:';

export const ENABLE_WITH_UNSAVED_CHANGES_MODAL_LABELS = {
  TITLE: 'Unsaved changes',
  MESSAGE: 'The index has unsaved changes. Would you like to save and enable?',
  OK_BUTTON_LABEL: 'Save & Enable',
};

// Rename index
export const INDEX_NAME_MODAL_LABELS = {
  MODAL_TITLE: 'Rename Index',
  NAME_INPUT_LABEL: 'Index name',
  OK_BUTTON_TEXT: 'Done',
};

export const INDEX_NAME_MODAL_INPUT_LABEL = 'Index name';
export const INDEX_NAME_MODAL_INPUT_PLACEHOLDER = 'Enter a unique index name here';

export const INDEX_DOCUMENTS_DOWNLOAD = {
  START_MESSAGE: 'Started downloading documents from index %indexName%.',
  SUCCESS_MESSAGE: 'Documents downloaded successfully. File name: %fileName%.',
};

export const UPGRADE_POPOVER_INDEX_CONTENT =
  'You can only create two indexes with the free plan and you’ve reached the limit.';
